// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CartPageTemplate, { CartPageTemplateStyles } from "@turnerBlueprint/App/Components/Cart/CartPageTemplate";
import XngZone from "@turnerBlueprint/App/Components/Common/XngZone";
import React from "react";
import { css } from "styled-components";

export interface CartPageContainerStyles {
    container?: InjectableCss;
    headerContainer?: InjectableCss;
    cartLinesContainer?: InjectableCss;
    fulfillmentMethodContainer?: InjectableCss;
    cartTotalContainer?: InjectableCss;
    productCarouselContainer?: InjectableCss;
    // XNG-Changes
    cartPageTemplateStyles?: CartPageTemplateStyles;
}

export const cartPageContainerStyles: CartPageContainerStyles = {
    container: {
        css: css`
            /* stylelint-disable value-no-vendor-prefix */
            display: -ms-grid;
            display: grid;
            padding: 1.5rem 0 0 1.5rem;
            margin: -1.5rem;
            overflow: hidden;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            grid-template-columns: minmax(0, 1fr);
                            -ms-grid-columns: minmax(0, 1fr);
                        `,
                        null,
                        css`
                            grid-template-columns: repeat(2, minmax(0, 1fr));
                            -ms-grid-columns: minmax(0, 1fr) minmax(0, 1fr);
                            grid-template-rows: repeat(5, auto);
                            -ms-grid-rows: auto auto auto auto auto;
                        `,
                        css`
                            grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
                            -ms-grid-columns: minmax(0, 2fr) minmax(0, 1fr);
                        `,
                    ],
                    "min",
                )}
        `,
    },
    headerContainer: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            border: 0 solid transparent;
                            border-width: 0 1.5rem 1.5rem 0;
                        `,
                        null,
                        css`
                            grid-column-start: 1;
                            -ms-grid-column: 1;
                            grid-column-end: 3;
                            -ms-grid-column-span: 3;
                            grid-row-start: 1;
                            -ms-grid-row: 1;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    cartLinesContainer: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            border: 0 solid transparent;
                            border-width: 0 1.5rem 1.5rem 0;
                        `,
                        null,
                        css`
                            grid-column-start: 1;
                            -ms-grid-column: 1;
                            grid-row-start: 2;
                            -ms-grid-row: 2;
                            grid-row-end: 6;
                            -ms-grid-row-span: 6;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    fulfillmentMethodContainer: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            border: 0 solid transparent;
                            border-width: 0 1.5rem 1.5rem 0;
                        `,
                        null,
                        css`
                            grid-column-start: 2;
                            -ms-grid-column: 2;
                            grid-row-start: 2;
                            -ms-grid-row: 2;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    cartTotalContainer: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            border: 0 solid transparent;
                            border-width: 0 1.5rem 1.5rem 0;
                        `,
                        null,
                        css`
                            grid-column-start: 2;
                            -ms-grid-column: 2;
                            grid-row-start: 3;
                            -ms-grid-row: 3;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    productCarouselContainer: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            border: 0 solid transparent;
                            border-width: 0 1.5rem 1.5rem 0;
                        `,
                        null,
                        css`
                            grid-column-start: 2;
                            -ms-grid-column: 2;
                            grid-row-start: 4;
                            -ms-grid-row: 4;
                        `,
                    ],
                    "min",
                )}
        `,
    },
};

const styles = cartPageContainerStyles;

const CartPageContainer = ({ id }: WidgetProps) => {
    // XNG-Changes
    const { cart } = useAppSelector(state => {
        return {
            cart: getCurrentCartState(state).value,
        };
    });
    const isEmptyCart = !cart || cart?.cartLines?.length === 0;

    // XNG-Changes: use CartPageTemplate
    return (
        <CartPageTemplate extendedStyles={styles.cartPageTemplateStyles}>
            <CartPageTemplate.HeaderSection>
                <CartPageTemplate.TitleSection>
                    <XngZone zoneName="Content0" contentId={id} hint="Cart - Title" />
                </CartPageTemplate.TitleSection>
                {!isEmptyCart && (
                    <CartPageTemplate.CartApproverMessage>
                        <XngZone zoneName="Content3" contentId={id} hint="Cart - Cart Messages" />
                    </CartPageTemplate.CartApproverMessage>
                )}
            </CartPageTemplate.HeaderSection>
            <CartPageTemplate.CartTotalSection>
                {!isEmptyCart && <XngZone zoneName="Content6" contentId={id} hint="Cart - FullfilmentMethodSelector" />}
                <XngZone zoneName="Content2" contentId={id} hint="Cart - CartTotal" />
            </CartPageTemplate.CartTotalSection>
            <CartPageTemplate.MainSection>
                {!isEmptyCart && (
                    <CartPageTemplate.ActionsSection>
                        <XngZone zoneName="Content4" contentId={id} hint="Cart - Cart Actions" />
                    </CartPageTemplate.ActionsSection>
                )}
                <XngZone zoneName="Content1" contentId={id} hint="Cart - CartLines table" />
            </CartPageTemplate.MainSection>
        </CartPageTemplate>
    );
};

const widgetModule: WidgetModule = {
    component: CartPageContainer,
    definition: {
        group: "Cart",
        allowedContexts: ["CartPage"],
        displayName: "Page Container",
    },
};

export default widgetModule;
